import { FC, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import {
  Button,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Container,
} from '@material-ui/core';
import { useHistory } from 'react-router';
import { Autocomplete } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import ControlPointIcon from '@material-ui/icons/ControlPoint';
import PreviewIcon from '@material-ui/icons/RemoveRedEyeSharp';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { orange, red } from '@material-ui/core/colors';
import axios from 'server';
import { IdpFormData, IdpRecord, idpDefaultValues } from './types';
import IconButton from '@material-ui/core/IconButton';
import { useStore } from 'hooks/useRootStore';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { handleCompanyUrl } from 'utils/organizationUtils';
const ThirdPartyTable: FC = () => {
  const history = useHistory();
  const {
    ldapStore: {
      departmentsView,
      getDepartments,
      getGroups,
      allGroupsView,
      usersView,
      getUsers,
      // getUserTimeline
    },
    notificationStore: { openNotification },
  } = useStore();
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { control, register, handleSubmit, reset, formState: { errors }, } = useForm<IdpFormData>({ defaultValues: idpDefaultValues });
  const {
    control: controlEdit,
    register: registerEdit,
    handleSubmit: handleSubmitEdit,
    reset: resetEdit,
    formState: { errors: errorsEdit },
  } = useForm<IdpFormData>();

  const {
    data: apiResponse,
    isLoading: isFetchingIdentity,
    isError: isIdentityError,
    refetch,
  } = useQuery(
    'saml-identity-providers',
    async () => {
      const response = await axios.get('saml/identity-providers');
      return response;
    },
    { refetchOnWindowFocus: false }
  );

  const getIdentityList = (): IdpRecord[] => {
    if (Array.isArray(apiResponse)) return apiResponse;
    if (Array.isArray(apiResponse?.data)) return apiResponse.data;
    if (Array.isArray(apiResponse?.data?.data)) return apiResponse.data.data;
    if (Array.isArray(apiResponse?.data?.items)) return apiResponse.data.items;
    return [];
  };
  const getUsersReq = useQuery('getUsersReq', () => getUsers(), {
    onError: () => openNotification('error', `Can't get users`),
  });
  const getDepartmentsReq = useQuery('getDepartmentsReq', () => getDepartments(), {
    onError: () => openNotification('error', `Can't get departments`),
  });
  const getGroupsReq = useQuery('getGroupsReq', () => getGroups(), {
    onError: () => openNotification('error', `Can't get groups`),
  });
  console.log("userView", usersView);
  console.log("departmentView", departmentsView);
  console.log("groupView", allGroupsView);

  const identityProviders = getIdentityList();
  const paginatedData = identityProviders.slice(page * perPage, page * perPage + perPage);

  const createIdp = useMutation((data: IdpFormData) => axios.post('saml/identity-providers', data), {
    onSuccess: () => {
      openNotification('success', 'Identity Provider added successfully');
      setOpen(false);
      reset(idpDefaultValues);
      refetch();
    },
    onError: (err: any) => {
      openNotification('error', err?.response?.data?.message || 'Something went wrong');
    },
  });
  const updateIdp = useMutation(
    ({ uuid, data }: { uuid: string; data: IdpFormData }) => axios.put(`saml/identity-providers/${uuid}`, data),
    {
      onSuccess: () => {
        openNotification('success', ' Update successfully');
      },

      onError: (error: any) => {
        const message = error?.response?.data?.message || error?.response?.data?.error || 'Failed to parse metadata';
        openNotification('error', message);
      },
    }
  );
  const deleteIdp = useMutation((uuid: string) => axios.delete(`saml/identity-providers/${uuid}`), {
    onSuccess: () => {
      openNotification('success', ' Remove successfully');
    },

    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.response?.data?.error || 'Failed to parse metadata';

      openNotification('error', message);
    },
  });

  const handleClose = () => {
    setOpen(false);
    reset(idpDefaultValues);
    createIdp.reset();
  };

  const onSubmit = (data: IdpFormData) => {
    createIdp.mutate({
      ...data,
      slo_url: data.slo_url || (null as any),
    });
  };


  const handleEdit = (idp: IdpRecord) => {
    setSelectedIdp(idp);
    resetEdit({
      name: idp.name,
      entity_id: idp.entity_id,
      sso_url: idp.sso_url,
      slo_url: idp.slo_url || '',
      x509_certificate: '',
      email_attribute: idp.email_attribute || '',
      is_active: idp.is_active,
      department_ids: idp.department_ids || [],
      group_ids: idp.group_ids || [],
      user_ids: idp.user_ids || [],
    });
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setSelectedIdp(null);
    updateIdp.reset();
  };
  const onEditSubmit = (data: IdpFormData) => {
    if (!selectedIdp) return;
    updateIdp.mutate(
      { uuid: selectedIdp.uuid, data: data as any },
      {
        onSuccess: (response) => {
          const updatedData = response?.data?.data || response?.data;
          setEditOpen(false);
          if (updatedData) {
            setSelectedIdp(updatedData);
          }
          refetch();
        },
      }
    );
  };

  const handleDeleteOpen = (idp: IdpRecord) => {
    setSelectedIdp(idp);
    setDeleteOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
    setSelectedIdp(null);
    deleteIdp.reset();
  };

  const handleDeleteConfirm = () => {
    if (!selectedIdp) return;
    deleteIdp.mutate(selectedIdp.uuid, {
      onSuccess: () => {
        setDeleteOpen(false);
        setSelectedIdp(null);
        refetch();
      },
    });
  };
  const [selectedIdp, setSelectedIdp] = useState<IdpRecord | null>(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const handleView = (idp: IdpRecord) => {
    setSelectedIdp(idp);
    setViewOpen(true);
  };

  const handleViewClose = () => {
    setViewOpen(false);
    setSelectedIdp(null);
  };
  useAppPreloader(isFetchingIdentity || createIdp.isLoading);
  return (
    <>
      <Grid container justify='flex-end' style={{ marginBottom: 16 }}>
        <Grid item>
          <Button
            variant='contained'
            color='secondary'
            style={{ backgroundColor: '#5081FF' }}
            onClick={() => setOpen(true)}
          >
            <ControlPointIcon />
          </Button>
        </Grid>
      </Grid>

      {isFetchingIdentity ? (
        <></>
      ) : isIdentityError ? (
        <Typography color='error' style={{ padding: 20 }}>
          Failed to load Third Party Providers.
        </Typography>
      ) : identityProviders.length === 0 ? (
        <Typography style={{ padding: 20, textAlign: 'center' }}>No Third Party Providers found.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Provider</strong>
                </TableCell>
                <TableCell>
                  <strong>Entity ID</strong>
                </TableCell>
                <TableCell align='right'>
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((idp) => (
                <TableRow key={idp.id} hover>
                  <TableCell>
                    <Typography style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>
                      {idp.name || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      style={{ color: '#6b7280', fontSize: 13, wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                    >
                      {idp.entity_id || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align='right'>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
                      <IconButton size='small' onClick={() => handleView(idp)}>
                        <PreviewIcon style={{ color: orange[500] }} />
                      </IconButton>
                      <IconButton size='small' onClick={() => handleEdit(idp)}>
                        <EditIcon style={{ color: orange[500] }} />
                      </IconButton>
                      <IconButton size='small' onClick={() => handleDeleteOpen(idp)}>
                        <DeleteIcon style={{ color: red[500] }} />
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {identityProviders.length > 0 && (
        <TablePagination
          component='div'
          count={identityProviders.length}
          page={page}
          onChangePage={(_: any, newPage: number) => setPage(newPage)}
          rowsPerPage={perPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onChangeRowsPerPage={(e: React.ChangeEvent<HTMLInputElement>) => {
            setPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          style={{ marginTop: 8 }}
        />
      )}

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
        <DialogTitle>Add Saml SP Provider</DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label='Name'
                  fullWidth
                  variant='outlined'
                  {...register('name', { required: 'Name is required' })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label='Entity ID'
                  fullWidth
                  variant='outlined'
                  {...register('entity_id', { required: 'Entity ID is required' })}
                  error={!!errors.entity_id}
                  helperText={errors.entity_id?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label='SSO URL'
                  fullWidth
                  variant='outlined'
                  {...register('sso_url', { required: 'SSO URL is required' })}
                  error={!!errors.sso_url}
                  helperText={errors.sso_url?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField label='SLO URL' fullWidth variant='outlined' {...register('slo_url')} />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label='X509 Certificate'
                  fullWidth
                  multiline
                  rows={5}
                  variant='outlined'
                  {...register('x509_certificate', { required: 'Certificate is required' })}
                  error={!!errors.x509_certificate}
                  helperText={errors.x509_certificate?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label='Email Attribute'
                  fullWidth
                  variant='outlined'
                  {...register('email_attribute', { required: 'Email attribute is required' })}
                  error={!!errors.email_attribute}
                  helperText={errors.email_attribute?.message}
                />
              </Grid>

              <Grid item xs={12}>
                {/* <p>Department</p> */}

                {!getDepartmentsReq.isFetching ? (
                  departmentsView.length === 0 ? (
                    <div>
                      <span>No Departments Found.</span>
                      <Button
                        onClick={() =>
                          history.push(handleCompanyUrl('/department/add'))
                        }
                      >
                        Add Department
                      </Button>
                    </div>
                  ) : (
                    <Controller
                      name="department_ids"
                      control={control}
                      render={({ field }) => {
                        const selectedDepartments = departmentsView.filter((department) =>
                          (field.value || []).includes(department.department_id)
                        );

                        return (
                          <Autocomplete
                            multiple
                            options={departmentsView}
                            disablePortal
                            value={selectedDepartments}
                            getOptionLabel={(option) => option.ou || ''}
                            isOptionEqualToValue={(option, value) =>
                              option.department_id === value.department_id
                            }
                            onChange={(_, values) => {
                              field.onChange(
                                values.map((value) => value.department_id)
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                label="Department"
                              />
                            )}
                          />
                        );
                      }}
                    />
                  )
                ) : (
                  <div>Loading...</div>
                )}
              </Grid>
              {/* Group */}
              <Grid item xs={12}>
                {!getGroupsReq.isFetching ? (
                  allGroupsView.length === 0 ? (
                    <div>
                      <span>No Groups Found.</span>
                      <Button
                        onClick={() =>
                          history.push(handleCompanyUrl('/group/add'))
                        }
                      >
                        Add Group
                      </Button>
                    </div>
                  ) : (
                    <Controller
                      name="group_ids"
                      control={control}
                      render={({ field }) => {
                        const selectedGroups = allGroupsView.filter((group) =>
                          (field.value || []).includes(group.group_id)
                        );

                        return (
                          <Autocomplete
                            multiple
                            options={allGroupsView}
                            disablePortal
                            value={selectedGroups}
                            getOptionLabel={(option) => option.cn || ''}
                            isOptionEqualToValue={(option, value) =>
                              option.group_id === value.group_id
                            }
                            onChange={(_, values) => {
                              field.onChange(
                                values.map((value) => value.group_id)
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                label="Groups"
                              />
                            )}
                          />
                        );
                      }}
                    />
                  )
                ) : (
                  <div>Loading...</div>
                )}
              </Grid>

              {/* Users */}
              <Grid item xs={12}>
                {!getUsersReq.isFetching ? (
                  usersView.length === 0 ? (
                    <div>
                      <span>No Users Found.</span>
                      <Button
                        onClick={() =>
                          history.push(handleCompanyUrl('/user/add'))
                        }
                      >
                        Add User
                      </Button>
                    </div>
                  ) : (
                    <Controller
                      name="user_ids"
                      control={control}
                      render={({ field }) => {
                        const selectedUsers = usersView.filter((user) =>
                          (field.value || []).includes(user.user_id)
                        );

                        return (
                          <Autocomplete
                            multiple
                            options={usersView}
                            disablePortal
                            value={selectedUsers}
                            getOptionLabel={(option) => option.cn || ''}
                            isOptionEqualToValue={(option, value) =>
                              option.user_id === value.user_id
                            }
                            onChange={(_, values) => {
                              field.onChange(
                                values.map((value) => value.id)
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                label="Users"
                              />
                            )}
                          />
                        );
                      }}
                    />
                  )
                ) : (
                  <div>Loading...</div>
                )}
              </Grid>
            </Grid>
            {createIdp.isError && (
              <Typography style={{ color: 'red', marginTop: 10 }}>
                {(createIdp.error as any)?.response?.data?.message || 'Something went wrong'}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={createIdp.isLoading}>
              Cancel
            </Button>
            <Button type='submit' variant='contained' color='primary' disabled={createIdp.isLoading}>
              {createIdp.isLoading ? <CircularProgress size={20} /> : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>


      <Dialog open={editOpen} onClose={handleEditClose} fullWidth maxWidth='sm'>
        <DialogTitle>Edit Identity Provider</DialogTitle>
        <form onSubmit={handleSubmitEdit(onEditSubmit)}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label='Name'
                  fullWidth
                  variant='outlined'
                  {...registerEdit('name', { required: 'Name is required' })}
                  error={!!errorsEdit.name}
                  helperText={errorsEdit.name?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label='Entity ID'
                  fullWidth
                  variant='outlined'
                  {...registerEdit('entity_id', { required: 'Entity ID is required' })}
                  error={!!errorsEdit.entity_id}
                  helperText={errorsEdit.entity_id?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label='SSO URL'
                  fullWidth
                  variant='outlined'
                  {...registerEdit('sso_url', { required: 'SSO URL is required' })}
                  error={!!errorsEdit.sso_url}
                  helperText={errorsEdit.sso_url?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField label='SLO URL' fullWidth variant='outlined' {...registerEdit('slo_url')} />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label='X509 Certificate'
                  fullWidth
                  multiline
                  rows={5}
                  variant='outlined'
                  placeholder='Leave empty to keep existing certificate'
                  {...registerEdit('x509_certificate')}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label='Email Attribute'
                  fullWidth
                  variant='outlined'
                  {...registerEdit('email_attribute', { required: 'Email attribute is required' })}
                  error={!!errorsEdit.email_attribute}
                  helperText={errorsEdit.email_attribute?.message}
                />
              </Grid>

              <Grid item xs={12}>
                {/* <p>Department</p> */}

                {!getDepartmentsReq.isFetching ? (
                  departmentsView.length === 0 ? (
                    <div>
                      <span>No Departments Found.</span>
                      <Button
                        onClick={() =>
                          history.push(handleCompanyUrl('/department/add'))
                        }
                      >
                        Add Department
                      </Button>
                    </div>
                  ) : (
                    <Controller
                      name="department_ids"
                      control={controlEdit}
                      render={({ field }) => {
                        const selectedDepartments = departmentsView.filter((department) =>
                          (field.value || []).includes(department.department_id)
                        );

                        return (
                          <Autocomplete
                            multiple
                            options={departmentsView}
                            disablePortal
                            value={selectedDepartments}
                            getOptionLabel={(option) => option.ou || ''}
                            isOptionEqualToValue={(option, value) =>
                              option.department_id === value.department_id
                            }
                            onChange={(_, values) => {
                              field.onChange(
                                values.map((value) => value.department_id)
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                label="Department"
                              />
                            )}
                          />
                        );
                      }}
                    />
                  )
                ) : (
                  <div>Loading...</div>
                )}
              </Grid>

              {/* Group */}
              <Grid item xs={12} >
                {/* <p>Group</p> */}

                {!getGroupsReq.isFetching ? (
                  allGroupsView.length === 0 ? (
                    <div>
                      <span>No Groups Found.</span>
                      <Button
                        onClick={() =>
                          history.push(handleCompanyUrl('/group/add'))
                        }
                      >
                        Add Group
                      </Button>
                    </div>
                  ) : (
                    <Controller
                      name="group_ids"
                      control={controlEdit}
                      render={({ field }) => {
                        const selectedGroups = allGroupsView.filter((group) =>
                          (field.value || []).includes(group.group_id)
                        );

                        return (
                          <Autocomplete
                            multiple
                            options={allGroupsView}
                            disablePortal
                            value={selectedGroups}
                            getOptionLabel={(option) => option.cn || ''}
                            isOptionEqualToValue={(option, value) =>
                              option.group_id === value.group_id
                            }
                            onChange={(_, values) => {
                              field.onChange(
                                values.map((value) => value.group_id)
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                label="Group"
                              />
                            )}
                          />
                        );
                      }}
                    />
                  )
                ) : (
                  <div>Loading...</div>
                )}
              </Grid>

              {/* Users */}
              <Grid item xs={12}  >
                {/* <p>Users</p> */}

                {!getUsersReq.isFetching ? (
                  usersView.length === 0 ? (
                    <div>
                      <span>No Users Found.</span>
                      <Button
                        onClick={() =>
                          history.push(handleCompanyUrl('/user/add'))
                        }
                      >
                        Add User
                      </Button>
                    </div>
                  ) : (
                    <Controller
                      name="user_ids"
                      control={controlEdit}
                      render={({ field }) => {

                        const selectedUsers = usersView.filter((user) =>
                          (field.value || []).includes(user.user_id)
                        );

                        return (
                          <Autocomplete
                            multiple
                            options={usersView}
                            disablePortal
                            value={selectedUsers}
                            getOptionLabel={(option) => option.cn || ''}
                            isOptionEqualToValue={(option, value) =>
                              option.cn === value.cn
                            }
                            onChange={(_, values) => {
                              field.onChange(
                                values.map((value) => value.user_id)
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                label="Users"
                              />
                            )}
                          />
                        );
                      }}
                    />
                  )
                ) : (
                  <div>Loading...</div>
                )}
              </Grid>
            </Grid>
            {updateIdp.isError && (
              <Typography style={{ color: 'red', marginTop: 10 }}>
                {(updateIdp.error as any)?.response?.data?.message || 'Something went wrong'}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleEditClose} disabled={updateIdp.isLoading}>
              Cancel
            </Button>
            <Button type='submit' variant='contained' color='primary' disabled={updateIdp.isLoading}>
              {updateIdp.isLoading ? <CircularProgress size={20} /> : 'Update'}
            </Button>
          </DialogActions>
        </form>
      </Dialog >

      <Dialog open={deleteOpen} onClose={handleDeleteClose} maxWidth='xs' fullWidth>
        <DialogTitle>Delete Identity Provider</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedIdp?.name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose} disabled={deleteIdp.isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant='contained'
            style={{ backgroundColor: red[500], color: '#fff' }}
            disabled={deleteIdp.isLoading}
          >
            {deleteIdp.isLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={viewOpen} onClose={handleViewClose} fullWidth maxWidth='md'>
        <DialogTitle>SAML SP Details</DialogTitle>

        <DialogContent>
          {selectedIdp && (
            <Grid container spacing={2}>
              {/* Identity Provider */}
              <Grid item xs={12}>
                <Typography
                  variant='subtitle1'
                  style={{
                    fontWeight: 600,
                    marginBottom: 4,
                  }}
                >
                  Basic Details
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label='Name'
                  value={selectedIdp.name || ''}
                  fullWidth
                  variant='outlined'
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label='UUID'
                  value={selectedIdp.uuid || ''}
                  fullWidth
                  variant='outlined'
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label='Entity ID'
                  value={selectedIdp.entity_id || ''}
                  fullWidth
                  variant='outlined'
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label='SSO URL'
                  value={selectedIdp.sso_url || ''}
                  fullWidth
                  variant='outlined'
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label='SLO URL'
                  value={selectedIdp.slo_url || ''}
                  fullWidth
                  variant='outlined'
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label='Email Attribute'
                  value={selectedIdp.email_attribute || ''}
                  fullWidth
                  variant='outlined'
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label='Status'
                  value={selectedIdp.is_active ? 'Active' : 'Inactive'}
                  fullWidth
                  variant='outlined'
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label='Certificate Fingerprint SHA-256'
                  value={selectedIdp.certificate_fingerprint_sha256 || ''}
                  fullWidth
                  variant='outlined'
                  InputProps={{
                    readOnly: true,
                    style: {
                      fontFamily: 'monospace',
                      fontSize: 13,
                    },
                  }}
                />
              </Grid>

              {/* Service Provider */}
              <Grid item xs={12} style={{ marginTop: 8 }}>
                <Typography
                  variant='subtitle1'
                  style={{
                    fontWeight: 600,
                    marginBottom: 4,
                  }}
                >
                  Service Provider Details
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label='Entity ID'
                  value={selectedIdp.sp?.entity_id || ''}
                  fullWidth
                  variant='outlined'
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label='Metadata URL'
                  value={selectedIdp.sp?.metadata_url || ''}
                  fullWidth
                  variant='outlined'
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label='ACS URL'
                  value={selectedIdp.sp?.acs_url || ''}
                  fullWidth
                  variant='outlined'
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label='Login URL'
                  value={selectedIdp.sp?.login_url || ''}
                  fullWidth
                  variant='outlined'
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleViewClose} color='primary' variant='contained'>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ThirdPartyTable;
